import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, MapPin, Check, UserRound, Sparkles, PawPrint, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { waLink } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppBubble";
import { useLang } from "@/context/LanguageContext";

const UI = {
  en: {
    intro: "Tell us a little about you and we'll get right back to you.",
    name: "Name *", email: "Email *", phone: "Phone (optional)",
    service: "What do you need help with? *",
    petsToggle: "Traveling with pets?",
    message: "Message *", messagePh: "How can we help with your move?",
    start: "Start chat", sending: "Sending…",
    typePh: "Type a message…",
    shareLoc: "Share my precise location (optional)", gettingLoc: "Getting location…", locShared: "Location shared",
    newChat: "New chat", savePets: "Save my pets",
    launcher: "Chat with us", close: "Close",
  },
  es: {
    intro: "Cuéntanos un poco sobre ti y te respondemos enseguida.",
    name: "Nombre *", email: "Correo electrónico *", phone: "Teléfono (opcional)",
    service: "¿Con qué necesitas ayuda? *",
    petsToggle: "¿Viajas con mascotas?",
    message: "Mensaje *", messagePh: "¿Cómo podemos ayudarte con tu mudanza?",
    start: "Iniciar chat", sending: "Enviando…",
    typePh: "Escribe un mensaje…",
    shareLoc: "Compartir mi ubicación exacta (opcional)", gettingLoc: "Obteniendo ubicación…", locShared: "Ubicación compartida",
    newChat: "Nuevo chat", savePets: "Guardar mis mascotas",
    launcher: "Escríbenos", close: "Cerrar",
  },
};

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const SERVICES = ["General question", "Moving consultation", "Route & pricing", "Pet transport", "Vehicle import", "Housing & cities", "Other"];
const STORE_KEY = "hm_chat_session";
const emptyChatPet = () => ({ name: "", species: "dog", temperament: "" });

const loadSession = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || null; } catch { return null; }
};

function ChatPetRows({ pets, setPets, ride, setRide }) {
  const set = (i, k, v) => setPets(pets.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-2 rounded-xl border border-sand-200 bg-sand-50 p-2.5" data-testid="chat-pet-rows">
      {pets.map((p, i) => (
        <div key={i} className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <input data-testid={`chat-pet-name-${i}`} className={FIELD} placeholder="Pet's name" value={p.name}
              onChange={(e) => set(i, "name", e.target.value)} />
            <select data-testid={`chat-pet-species-${i}`} className={`${FIELD} w-24 shrink-0`} value={p.species}
              onChange={(e) => set(i, "species", e.target.value)}>
              <option value="dog">Dog</option><option value="cat">Cat</option><option value="other">Other</option>
            </select>
            <button type="button" data-testid={`chat-pet-remove-${i}`} aria-label="Remove pet"
              onClick={() => setPets(pets.filter((_, j) => j !== i))}
              className="shrink-0 p-1.5 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
          </div>
          <input data-testid={`chat-pet-note-${i}`} className={FIELD} placeholder="Anything we should know? (anxious in cars, meds…)"
            value={p.temperament} onChange={(e) => set(i, "temperament", e.target.value)} />
        </div>
      ))}
      <label className="flex items-center gap-2 rounded-lg bg-white border border-sand-200 px-2.5 py-2 text-xs font-medium text-charcoal-700">
        <input data-testid="chat-pet-ride-toggle" type="checkbox" className="accent-terracotta"
          checked={!!ride} onChange={(e) => setRide(e.target.checked)} />
        Want us to drive your pets down for you? 🚗🐾
      </label>
      <button type="button" data-testid="chat-pet-add" onClick={() => setPets([...pets, emptyChatPet()])}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-terracotta hover:underline">
        <Plus size={12} /> Add another pet
      </button>
    </div>
  );
}


const LINK_RE = /(https?:\/\/[^\s)]+|\/pet-transport\b)/g;

function LinkedText({ text }) {
  return String(text || "").split(LINK_RE).map((part, i) => {
    if (part === "/pet-transport" || /^https?:\/\//.test(part)) {
      const internal = part.startsWith("/");
      return (
        <a key={i} data-testid="chat-msg-link" href={part}
          target={internal ? "_self" : "_blank"} rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 hover:opacity-80">
          {internal ? "Pet Transport guide" : part}
        </a>
      );
    }
    return part;
  });
}

function Bubble({ m, name }) {
  const style = m.from === "visitor" ? "bg-terracotta text-white rounded-br-sm"
    : m.from === "admin" ? "bg-charcoal-800 text-white rounded-bl-sm"
      : m.from === "bot" ? "bg-white border border-sand-200 text-charcoal-800 rounded-bl-sm"
        : "bg-sage-light text-charcoal-700 rounded-bl-sm";
  return (
    <div className={`flex ${m.from === "visitor" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug whitespace-pre-wrap ${style}`}>
        {m.from === "admin" && <p className="mb-0.5 text-[10px] font-medium text-white/70 inline-flex items-center gap-1"><UserRound size={10} /> Hola Movers team</p>}
        {m.from === "bot" && <p className="mb-0.5 text-[10px] font-medium text-charcoal-400 inline-flex items-center gap-1"><Sparkles size={10} /> AI assistant</p>}
        <LinkedText text={m.text} />
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(loadSession);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0], message: "" });
  const [messages, setMessages] = useState([]);
  const [chatState, setChatState] = useState({ escalated: false, human_takeover: false, ai_enabled: true, handoff_enabled: true });
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [geoShared, setGeoShared] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [withPets, setWithPets] = useState(false);
  const [pets, setPets] = useState([]);
  const [chatPets, setChatPets] = useState([]);
  const [petPanel, setPetPanel] = useState(false);
  const [petRide, setPetRide] = useState(false);
  const endRef = useRef(null);
  const { lang } = useLang();
  const ui = UI[lang] || UI.en;

  const togglePets = (on) => {
    setWithPets(on);
    setPets(on ? (pets.length ? pets : [emptyChatPet()]) : []);
  };

  useEffect(() => {
    if (open && !config) api.get("/chat/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, [open, config]);

  useEffect(() => {
    const onOpenChat = (e) => {
      setOpen(true);
      if (e.detail?.petRide) {
        setPetRide(true);
        if (loadSession()) {
          setPetPanel(true);
          setPets((p) => (p.length ? p : [emptyChatPet()]));
        } else {
          setForm((f) => ({ ...f, service: "Pet transport" }));
          setWithPets(true);
          setPets((p) => (p.length ? p : [emptyChatPet()]));
        }
      }
    };
    window.addEventListener("hola:open-chat", onOpenChat);
    return () => window.removeEventListener("hola:open-chat", onOpenChat);
  }, []);

  const poll = useCallback(async () => {
    const s = loadSession();
    if (!s) return;
    try {
      const { data } = await api.get(`/chat/${s.id}`, { params: { token: s.token } });
      setMessages(data.messages);
      setGeoShared(data.geo_consent);
      setChatPets(data.pets || []);
      setChatState({ escalated: data.escalated, human_takeover: data.human_takeover, ai_enabled: data.ai_enabled, handoff_enabled: data.handoff_enabled });
    } catch (err) {
      if (err?.response?.status === 404) {
        localStorage.removeItem(STORE_KEY);
        setSession(null);
        setMessages([]);
      }
    }
  }, []);

  useEffect(() => {
    if (!open || !session) return;
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [open, session, poll]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, botTyping]);

  const botActive = chatState.ai_enabled && !chatState.escalated && !chatState.human_takeover;

  const start = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/start", {
        ...form,
        traveling_with_pets: withPets,
        pets: pets.filter((p) => p.name || p.temperament),
        pet_transport_interest: petRide,
        page_url: window.location.href,
        referrer: document.referrer || "",
        lang,
      });
      const s = { id: data.id, token: data.token, name: form.name };
      localStorage.setItem(STORE_KEY, JSON.stringify(s));
      setMessages([{ from: "visitor", text: form.message, at: new Date().toISOString() }]);
      setSession(s);
      setBotTyping(!!config?.ai_enabled);
      setTimeout(() => setBotTyping(false), 15000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send — please try again.");
    } finally { setSending(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !session || sending) return;
    setDraft("");
    setMessages((m) => [...m, { from: "visitor", text, at: new Date().toISOString() }]);
    if (botActive) setBotTyping(true);
    try {
      const { data } = await api.post(`/chat/${session.id}/message`, { token: session.token, message: text });
      const extras = (data.messages || []).filter((m) => m.from !== "visitor");
      if (extras.length) setMessages((m) => [...m, ...extras]);
      poll();
    } catch { setError("Message failed to send — please retry."); }
    finally { setBotTyping(false); }
  };

  const talkToHuman = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/chat/${session.id}/escalate`, { token: session.token, message: "Visitor tapped 'Talk to a person'" });
      if (data.message) setMessages((m) => [...m, data.message]);
      poll();
    } catch { /* poll will catch up */ }
  };

  const shareLocation = () => {
    if (!navigator.geolocation || !session) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post(`/chat/${session.id}/location`, {
            token: session.token, lat: pos.coords.latitude, lon: pos.coords.longitude,
          });
          setGeoShared(true);
        } catch { /* chat keeps working */ }
        setGeoBusy(false);
      },
      () => setGeoBusy(false),
      { timeout: 8000 },
    );
  };

  const savePetsInChat = async () => {
    if (!session) return;
    const cleaned = pets.filter((p) => p.name || p.temperament);
    if (!cleaned.length) return setError("Tell us at least your pet's name.");
    setError("");
    try {
      const { data } = await api.post(`/chat/${session.id}/pets`, { token: session.token, pets: cleaned, pet_transport_interest: petRide });
      if (data.message) setMessages((m) => [...m, data.message]);
      setChatPets(data.pets || cleaned);
      setPetPanel(false);
    } catch (err) { setError(err?.response?.data?.detail || "Could not save your pets — please retry."); }
  };

  const endChat = () => {
    localStorage.removeItem(STORE_KEY);
    setSession(null);
    setMessages([]);
    setChatPets([]);
    setPetPanel(false);
    togglePets(false);
    setForm({ name: "", email: "", phone: "", service: SERVICES[0], message: "" });
  };

  return (
    <>
      {open && (
        <div data-testid="chat-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 mb-16 lg:mb-0 flex w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-[0_16px_48px_rgba(44,42,41,0.22)]"
          style={{ maxHeight: "min(70vh, 560px)" }}>
          <div className="flex items-center justify-between bg-charcoal-800 px-4 py-3 text-white">
            <div>
              <p className="text-sm font-medium">{lang === "es" ? "Chatea con Hola Movers" : "Chat with Hola Movers"}</p>
              <p className="text-[11px] text-white/70">
                {config?.ai_enabled
                  ? (lang === "es" ? "La IA responde al instante · personas reales si lo pides" : "AI answers instantly · humans on request")
                  : (lang === "es" ? "Normalmente respondemos en unas horas hábiles" : "We usually reply within a few business hours")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <a data-testid="chat-whatsapp-link" href={waLink("Hola! I have a question about moving to Mexico.")} target="_blank" rel="noopener noreferrer"
                aria-label="Chat on WhatsApp" title="Prefer WhatsApp?"
                className="rounded-full p-1.5 text-[#25D366] hover:bg-white/10"><WhatsAppIcon size={18} /></a>
              <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Close chat" className="rounded-full p-1.5 hover:bg-white/10"><X size={16} /></button>
            </div>
          </div>

          {!session ? (
            <form onSubmit={start} className="flex-1 space-y-2.5 overflow-y-auto p-4">
              <p className="text-sm text-charcoal-600">{ui.intro}</p>
              <label className="block text-xs font-medium text-charcoal-600">{ui.name}
                <input data-testid="chat-name" required className={`${FIELD} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
              <label className="block text-xs font-medium text-charcoal-600">{ui.email}
                <input data-testid="chat-email" type="email" required className={`${FIELD} mt-1`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
              <label className="block text-xs font-medium text-charcoal-600">{ui.phone}
                <input data-testid="chat-phone" className={`${FIELD} mt-1`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
              <label className="block text-xs font-medium text-charcoal-600">{ui.service}
                <select data-testid="chat-service" className={`${FIELD} mt-1`} value={form.service}
                  onChange={(e) => {
                    setForm({ ...form, service: e.target.value });
                    if (e.target.value === "Pet transport" && !withPets) togglePets(true);
                  }}>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label className="flex items-center gap-2 text-xs font-medium text-charcoal-600">
                <input data-testid="chat-pets-toggle" type="checkbox" className="accent-terracotta"
                  checked={withPets} onChange={(e) => togglePets(e.target.checked)} />
                {ui.petsToggle} <PawPrint size={12} className="text-terracotta" />
              </label>
              {withPets && <ChatPetRows pets={pets} setPets={setPets} ride={petRide} setRide={setPetRide} />}
              <label className="block text-xs font-medium text-charcoal-600">{ui.message}
                <textarea data-testid="chat-message" required rows={3} className={`${FIELD} mt-1`} placeholder={ui.messagePh} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></label>
              {error && <p data-testid="chat-error" className="text-xs text-destructive">{error}</p>}
              <button data-testid="chat-start" type="submit" disabled={sending}
                className="w-full rounded-full bg-terracotta px-4 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
                {sending ? ui.sending : ui.start}
              </button>
              <p className="text-[10px] leading-snug text-charcoal-400">
                {config?.privacy_text || "By starting a chat you agree we may store your message and an approximate, city-level location (from your IP) to route your enquiry. Precise location is only used if you explicitly share it."}
              </p>
            </form>
          ) : (
            <>
              <div data-testid="chat-messages" className="flex-1 space-y-2 overflow-y-auto bg-sand-50 p-4">
                {messages.map((m, i) => <Bubble key={i} m={m} name={session.name} />)}
                {botTyping && (
                  <div data-testid="chat-typing" className="flex justify-start">
                    <div className="rounded-2xl rounded-bl-sm border border-sand-200 bg-white px-4 py-2.5">
                      <span className="inline-flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-300" style={{ animationDelay: `${i * 150}ms` }} />
                        ))}
                      </span>
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <div className="border-t border-sand-200 bg-white p-3">
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  {botActive && chatState.handoff_enabled && (
                    <button data-testid="chat-talk-human" onClick={talkToHuman}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-[11px] font-medium text-charcoal-600 hover:border-terracotta hover:text-terracotta">
                      <UserRound size={12} /> Talk to a person
                    </button>
                  )}
                  {chatPets.length === 0 ? (
                    <button data-testid="chat-pets-chip" onClick={() => { setPetPanel((v) => !v); if (!pets.length) setPets([emptyChatPet()]); }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-[11px] font-medium text-charcoal-600 hover:border-terracotta hover:text-terracotta">
                      <PawPrint size={12} /> Traveling with pets?
                    </button>
                  ) : (
                    <span data-testid="chat-pets-saved" className="inline-flex items-center gap-1.5 rounded-full bg-sage-light px-3 py-1.5 text-[11px] font-medium text-sage-700">
                      <PawPrint size={12} /> {chatPets.map((p) => p.name).filter(Boolean).join(", ") || "Pets noted"} 🐾
                    </span>
                  )}
                </div>
                {petPanel && chatPets.length === 0 && (
                  <div className="mb-2 space-y-2" data-testid="chat-pets-panel">
                    <ChatPetRows pets={pets} setPets={setPets} ride={petRide} setRide={setPetRide} />
                    <button data-testid="chat-pets-save" onClick={savePetsInChat}
                      className="w-full rounded-full bg-terracotta px-4 py-2 text-xs font-medium text-white hover:bg-terracotta-hover">
                      {ui.savePets}
                    </button>
                    {error && <p className="text-[11px] text-destructive">{error}</p>}
                  </div>
                )}
                <form onSubmit={send} className="flex items-center gap-2">
                  <input data-testid="chat-input" className={FIELD} placeholder={ui.typePh} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Chat message" />
                  <button data-testid="chat-send" type="submit" aria-label="Send message"
                    className="shrink-0 rounded-full bg-terracotta p-2.5 text-white hover:bg-terracotta-hover"><Send size={16} /></button>
                </form>
                <div className="mt-2 flex items-center justify-between">
                  {geoShared ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-sage-700"><Check size={12} /> {ui.locShared}</span>
                  ) : (
                    <button data-testid="chat-share-location" onClick={shareLocation} disabled={geoBusy}
                      className="inline-flex items-center gap-1 text-[11px] text-charcoal-500 underline underline-offset-2 hover:text-terracotta disabled:opacity-50">
                      <MapPin size={12} /> {geoBusy ? ui.gettingLoc : ui.shareLoc}
                    </button>
                  )}
                  <button data-testid="chat-end" onClick={endChat} className="text-[11px] text-charcoal-400 underline underline-offset-2 hover:text-destructive">{ui.newChat}</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <button data-testid="chat-launcher" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-4 sm:right-6 z-50 mb-16 lg:mb-0 flex items-center gap-2 rounded-full bg-terracotta py-3.5 pl-4 pr-5 text-white shadow-[0_6px_24px_rgba(196,90,58,0.45)] transition-transform hover:scale-105">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        <span className="hidden text-sm font-medium sm:inline">{open ? ui.close : ui.launcher}</span>
      </button>
    </>
  );
}
